import React, { useContext } from 'react';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  CircularProgress,
  Button,
} from '@material-ui/core';
import Card from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import clsx from 'clsx';
import { useTranslation } from '../../../../hooks/useTranslation';
import useAuth from '../../../../hooks/useAuth';
import colors from '../../../../themes/main-colors';
import OutageMapPanelHeader from './header/OutageMapPanelHeader';
import TerritorySummaryBlock from './summary/TerritorySummaryBlock';
import PersonalSummaryBlock from './summary/PersonalSummaryBlock';
import SignInBlock from './user-details/SignInBlock';
import UserDetailsBlock from './user-details/UserDetailsBlock';
import Link from '../../../text-link';
import OutagePanelLayout from './outage-panel-layout/OutagePanelLayout';
import {
  AccountDetail,
  Maybe,
  OutageLookupResponse,
  Scalars,
} from '../../../../__generated__/pge-types';
import { toDateAtTimeString } from '../../../../util/format';
import useUtils from '../utils';
import { NotificationRequestAccountParams } from '../../OutageCenterPage';
import { useIsMobile } from './../../../../util/style-utils';
import { DrawerSnapType } from '../bottom-drawer-layout/BottomDrawerLayout';
import ReportOutageIcon from '../../../../static/reportOutageSymbol.svg';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import OutageCountIcon from '../../../../static/outageCountIcon.svg';
import { hideOutageByZipCode } from '../../outageUtils';
import { BackButtonComponent } from '../outage-report-popup/OutageLocationLookup';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      width: '90%',
      margin: '1rem 0.8rem',
      [theme.breakpoints.down('sm')]: {
        margin: '1rem',
      },
    },
    loggedInButton: {
      border: `2px solid ${colors.sparkBlue}`,
      borderRadius: 50,
      color: colors.sparkBlue,
      whiteSpace: 'nowrap',
      '&:hover': {
        background: colors.white,
        border: `2px solid ${colors.sparkBlue}`,
        boxShadow:
          '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 5px 8px 0px rgb(0 0 0 / 14%), 0px 1px 14px 0px rgb(0 0 0 / 12%)',
      },
    },
    cardItem: {
      boxShadow: 'none',
      padding: '0.75rem',
      margin: '10px 0 0',
    },

    cardDetails: {
      background: colors.modalBlue,
      boxShadow: 'none',
      padding: '0.75rem 0.75rem 0 0.75rem',
    },

    cardOverflow: {
      overflowY: 'auto',
      minHeight: 80,
    },
    outageMapPanelContent: {
      overflow: 'hidden',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: 'initial',
      },
    },
    userDetailsBlock: {
      width: '100%',
      borderRadius: '4px',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '30vh',
        overflow: 'auto',
      },
    },
    buttonContainer: {
      padding: '1rem 1rem 0rem 1rem',
      width: '100%',
      justifyContent: 'space-evenly',
    },
    ReportOutageButtonMob: {
      fontSize: '1.1rem',
      borderRadius: 18,
      color: colors.white,
      left: '50%',
      width: '90%',
      height: '42px',
      transform: 'translate(-50%)',
      background: colors.orange,
      letterSpacing: 0.32,
      margin: '0rem 4rem 0rem 0rem',
      '&:hover': {
        boxShadow: theme.shadows[5],
        background: colors.orange,
      },
    },
    reportMobBtnIcon: {
      marginRight: '5px',
      '& .path-a': {
        fill: colors.white,
      },
      '& .path-b': {
        stroke: colors.white,
      },
    },
    userDetailsDivider: {
      margin: '1rem 0 0 0',
      border: '1px solid #fff',
    },
    userDetailsSpacer: {
      margin: '0 0 1rem 0',
      padding: '0 0 0.75rem 0',
      border: `1px solid ${colors.modalBlue}`,
    },
    iconDescriptionWrapper: {
      display: 'flex',
      paddingTop: '0.875rem',
      justifyContent: 'center',
    },
    outageCountIcon: {
      width: '1.5rem',
      height: '1.5rem',
      minWidth: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        width: '2rem',
        height: '2rem',
      },
    },
    iconDescriptionText: {
      paddingLeft: '0.5rem',
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        marginTop: '6px',
      },
    },
    mainPanelBodyContent: {
      [theme.breakpoints.up('sm')]: {
        maxHeight: '95%',
        overflow: 'auto',
      },
    },
    greyCard: {
      background: colors.lightGray3,
    },
    blueCard: {
      background: colors.modalBlue,
    },
    borderRadiusBottom: {
      borderRadius: '0px 0px 4px 4px',
    },
  }),
);

interface Props {
  handleOutageByZipClick: (show: boolean) => void;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
  isNativeMobileAppPage?: boolean;
  accountDetails?: AccountDetail[];
  accountDetailsLoading?: boolean;
  totalOutages: number;
  customersAffected: number;
  lastUpdateOutageDataTime: Maybe<Scalars['DateTimeISO']>;
  setSinglePremise: (
    data: OutageLookupResponse,
    accountNumber?: string,
    accountIdentifier?: string,
  ) => void;
  setMultiPremise: (
    data: OutageLookupResponse,
    accountNumber?: string,
    accountIdentifier?: string,
  ) => void;
  setSelectedEncryptedPremiseId?: (
    encryptedPremiseId: string | null | undefined,
  ) => void;
  faqNoticePopup: () => void;
  updateReportOutagePopup: () => void;
  setOtherLocationType: () => void;
  setNotifFormSuccessfullySubmitted: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  onClose: () => void;
}

export const OutageMapPanel = (props: Props) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const { isPlannedDownTime } = useContext(featureFlagsContext);

  const {
    handleOutageByZipClick,
    isNativeMobileAppPage,
    accountDetails,
    lastUpdateOutageDataTime,
    totalOutages,
    customersAffected,
    setSinglePremise,
    setMultiPremise,
    setLoggedInUserNotificationRequestAccountParams,
    setSelectedEncryptedPremiseId,
    accountDetailsLoading,
    faqNoticePopup,
    updateReportOutagePopup,
    setOtherLocationType,
    setNotifFormSuccessfullySubmitted,
    onClose,
  } = props;

  const classes = useStyles();

  setNotifFormSuccessfullySubmitted(false);

  const {
    getOutageAccountDetails,
    buildLeftPanelDataFromAccountPremise,
  } = useUtils();
  const accountsDisplay =
    accountDetails && accountDetails.length
      ? getOutageAccountDetails(accountDetails)
      : [];

  const hasKnownOutage =
    accountsDisplay && accountsDisplay.some(account => account.hasKnownOutage);
  const lastUpdatedTime = lastUpdateOutageDataTime
    ? toDateAtTimeString(new Date(lastUpdateOutageDataTime))
    : '';

  return (
    <OutagePanelLayout
      onClose={onClose}
      isMainPanel
      refId={0}
      defaultSnap={
        isAuthenticated && accountsDisplay && accountsDisplay?.length > 1
          ? DrawerSnapType.maxHeight
          : DrawerSnapType.default
      }
      footer={
        !isPlannedDownTime ? (
          <Grid item className={classes.buttonContainer}>
            <Button
              size={'large'}
              className={classes.ReportOutageButtonMob}
              onClick={updateReportOutagePopup}
            >
              <ReportOutageIcon className={classes.reportMobBtnIcon} />
              <Typography variant={'h4'} noWrap>
                {t('REPORT_OUTAGE')}
              </Typography>
            </Button>
            <BackButtonComponent onPrevious={onClose} />
          </Grid>
        ) : null
      }
    >
      <Grid item>
        <OutageMapPanelHeader
          showHomeIcon={isAuthenticated}
          isAuthenticated={isAuthenticated}
          hasKnownOutage={!!hasKnownOutage}
          outageCount={totalOutages}
        />
      </Grid>
      <Grid className={classes.mainPanelBodyContent}>
        <Grid item data-testid="SummaryBlock">
          {isAuthenticated && (
            <PersonalSummaryBlock lastupdated={lastUpdatedTime} />
          )}
          {!isAuthenticated && (
            <TerritorySummaryBlock
              totalOutages={totalOutages !== -1 ? totalOutages : 0}
              customersAffected={customersAffected}
              lastupdated={lastUpdatedTime}
            />
          )}
        </Grid>
        <Grid
          item
          className={clsx(
            classes.userDetailsBlock,
            !isMobile &&
              isAuthenticated &&
              accountsDisplay &&
              accountsDisplay.length > 0
              ? classes.cardOverflow
              : '',
          )}
          data-testid="UserDetailsBlock"
        >
          {isAuthenticated && accountsDisplay?.length
            ? accountsDisplay.map((account: any, idx: number, { length }) => {
                const isLastIdx: boolean = idx === length - 1;

                return (
                  <Card
                    className={clsx(
                      classes.cardDetails,
                      isLastIdx ? classes.borderRadiusBottom : '',
                    )}
                    key={idx}
                    square={true}
                  >
                    <UserDetailsBlock
                      accountIdentifier={account.description}
                      accountNumber={account.accountNumber}
                      encryptedPersonId={account.encryptedPersonId}
                      isPrimary={account.isPrimaryAccount}
                      hasKnownOutage={account.hasKnownOutage}
                      addressLine1={account.addressLine1}
                      premiseInfos={account.premiseInfos}
                      setSinglePremise={setSinglePremise}
                      setMultiPremise={setMultiPremise}
                      setLoggedInUserNotificationRequestAccountParams={
                        setLoggedInUserNotificationRequestAccountParams
                      }
                      setSelectedEncryptedPremiseId={
                        setSelectedEncryptedPremiseId
                      }
                    />

                    <hr
                      className={
                        isLastIdx
                          ? classes.userDetailsSpacer
                          : classes.userDetailsDivider
                      }
                    />
                  </Card>
                );
              })
            : accountDetailsLoading && (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Box>
              )}
          {isAuthenticated &&
            !accountDetailsLoading &&
            !accountsDisplay.length && (
              <Card className={clsx(classes.cardItem, classes.blueCard)}>
                <Typography variant="subtitle1">
                  {t('ACCOUNT_NOT_FOUND')}
                </Typography>
              </Card>
            )}
          {!isPlannedDownTime && !isAuthenticated && !isNativeMobileAppPage && (
            <Card className={clsx(classes.cardItem, classes.blueCard)}>
              <SignInBlock />
            </Card>
          )}
        </Grid>

        {isAuthenticated && (
          <Grid item data-testid="ReportOutageOtherLocation">
            <Card className={clsx(classes.cardItem, classes.greyCard)}>
              <Box style={{ textAlign: 'center' }}>
                <Typography
                  variant="subtitle2"
                  data-testid="Report-outagae-other-location"
                >
                  {t('WANT_TO_REPORT_OUTAGE_FOR_OTHER_LOCATION')}
                </Typography>
                <Link
                  to={'/'}
                  variant="body2"
                  // className={classes.reportMobBtn}
                  onClick={e => {
                    e.preventDefault();
                    setOtherLocationType && setOtherLocationType();
                  }}
                >
                  <Typography variant="subtitle2">
                    {t('REPORT_OUTAGE_ANOTHER_LOCATION_HYPERLINK')}
                  </Typography>
                </Link>
              </Box>
            </Card>
          </Grid>
        )}
        <Grid item data-testid="FAQNotices">
          <Card className={clsx(classes.cardItem, classes.blueCard)}>
            <Box style={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" data-testid="FAQ-accordion">
                {t('FAQ_GOT_QUESTIONS')}
              </Typography>
              <Link
                to={'/'}
                variant="body2"
                onClick={e => {
                  e.preventDefault();
                  faqNoticePopup && faqNoticePopup();
                }}
              >
                <Typography variant="subtitle2">{t('OPEN_FAQ')}</Typography>
              </Link>
            </Box>
          </Card>
        </Grid>
        {!hideOutageByZipCode && (
          <Grid item data-testid="ZipCodeBlock">
            <Card className={clsx(classes.cardItem, classes.greyCard)}>
              <Box style={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" data-testid="OMP_PGServiceArea">
                  {t('PORTLAND_GENERAL_SERVICE_AREA')}
                </Typography>
                <Link
                  to={'/'}
                  variant="body2"
                  onClick={e => {
                    e.preventDefault();
                    handleOutageByZipClick && handleOutageByZipClick(true);
                  }}
                >
                  {t('OUTAGE_LIST_BY_ZIP_CODE')}
                </Link>
              </Box>
            </Card>
          </Grid>
        )}
        <Grid item>
          <Box className={classes.iconDescriptionWrapper}>
            <OutageCountIcon className={classes.outageCountIcon} />
            <Typography variant="body2" className={classes.iconDescriptionText}>
              {'= ' + t('NUMBER_OF_CUSTOMERS_AFFECTED')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </OutagePanelLayout>
  );
};

export default OutageMapPanel;
